/**
 * Message Security Configuration
 * Derives security rules from documentSecurity.js (same rules, single source of truth).
 * Display config (labels, colors, icons) is message-specific.
 */

import {
  getAllowedRolesForSecurityLevel,
  getAllowedSecurityLevelsForRole as getDocSecurityLevelsForRole,
  canUserAccessSecurityLevel,
  getHighestAllowedSecurityLevelForRole as getDocHighestLevel,
} from './documentSecurity'

// Security level definitions for messages (display config only, roles derived from API)
export const MESSAGE_SECURITY_LEVELS = {
  RED: {
    value: 'red',
    label: 'Red - Highly Confidential',
    color: 'red',
    bgColor: 'bg-red-100',
    textColor: 'text-red-800',
    borderColor: 'border-red-300',
    description: 'Extremely sensitive information. Owner and Admin only.',
    icon: '🔴',
    get roles() { return getAllowedRolesForSecurityLevel('red') }
  },
  YELLOW: {
    value: 'yellow',
    label: 'Yellow - Confidential',
    color: 'yellow',
    bgColor: 'bg-yellow-100',
    textColor: 'text-yellow-800',
    borderColor: 'border-yellow-300',
    description: 'Sensitive information. Owner, Admin, Diamond, and Gold.',
    icon: '🟡',
    get roles() { return getAllowedRolesForSecurityLevel('yellow') }
  },
  GREEN: {
    value: 'green',
    label: 'Green - General',
    color: 'green',
    bgColor: 'bg-green-100',
    textColor: 'text-green-800',
    borderColor: 'border-green-300',
    description: 'General information. All team members.',
    icon: '🟢',
    get roles() { return getAllowedRolesForSecurityLevel('green') }
  }
}

// Role-based security level access (derived dynamically)
export const ROLE_MESSAGE_ACCESS = new Proxy({}, {
  get(_, role) {
    return getDocSecurityLevelsForRole(role)
  }
})

/**
 * Get allowed security levels for a given role
 */
export function getAllowedSecurityLevelsForRole(role) {
  return getDocSecurityLevelsForRole(role)
}

/**
 * Get the highest allowed security level for a role
 */
export function getHighestAllowedSecurityLevelForRole(role) {
  return getDocHighestLevel(role)
}

/**
 * Check if a role can access a specific security level
 */
export function canRoleAccessSecurityLevel(role, securityLevel) {
  return canUserAccessSecurityLevel(role, securityLevel)
}

/**
 * Get security level configuration by value
 */
export function getSecurityLevelConfig(level) {
  const normalizedLevel = level?.toUpperCase()
  return MESSAGE_SECURITY_LEVELS[normalizedLevel] || MESSAGE_SECURITY_LEVELS.GREEN
}

/**
 * Get all security level options as array
 */
export function getAllSecurityLevelOptions() {
  return Object.values(MESSAGE_SECURITY_LEVELS)
}

/**
 * Get security level options allowed for a role
 */
export function getSecurityLevelOptionsForRole(role) {
  const allowedLevels = getAllowedSecurityLevelsForRole(role)
  return getAllSecurityLevelOptions().filter(level =>
    allowedLevels.includes(level.value)
  )
}

// Message scope types
export const MESSAGE_SCOPES = {
  DIRECT: {
    value: 'direct',
    label: 'Direct Message',
    description: 'Send to specific case, client, or session',
    icon: '📧'
  },
  BROADCAST: {
    value: 'broadcast',
    label: 'Broadcast',
    description: 'Send to all workspace members',
    icon: '📢'
  }
}

/**
 * Get message scope configuration
 */
export function getMessageScopeConfig(scope) {
  const normalizedScope = scope?.toUpperCase()
  return MESSAGE_SCOPES[normalizedScope] || MESSAGE_SCOPES.DIRECT
}
